'use client';

import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { Client } from '@zg-rentals/http-client';
import type { ExternalLinkWarningData, PersonalContactDetailsWarningData } from '@zg-rentals/satellite-components';

import type { InboxAnalyticsEvent } from './events';

export type { InboxAnalyticsEvent } from './events';

/**
 * Per-conversation listing context surfaced to the consumer's `getListingHref` resolver.
 *
 * The satellite-api conversation DTO carries only `listingAlias` (opaque base33 id) and
 * `syndicationUrl` (renter-side only, points at the Zillow-brand HDP). Different consumers
 * resolve these into a brand-specific URL differently — Zillow opens `syndicationUrl` in a
 * new tab; HotPads needs a `/<slug>/pad` URL the API doesn't currently provide. The shape
 * of `ListingLinkContext` will grow as future DTO fields become useful here.
 *
 * See HP-8069 for the in-flight ask to extend the DTO with a HotPads-friendly URL field.
 */
export type ListingLinkContext = {
  listingAlias: string;
  syndicationUrl?: string;
};

/**
 * Consumer-supplied configuration for the inbox module. Everything in here is genuinely
 * owned by the host application — identity (`currentUser`), routing (`basePath`,
 * `onUnauthorized`), and brand-specific URL semantics (`getListingHref`). Everything else
 * (analytics events, logging scopes, toast surface, WebSocket events, API endpoints) lives
 * inside `@zg-rentals/satellite-inbox` itself and is Satellite-team-owned.
 */
export type InboxConfig = {
  /** Per-consumer route prefix, e.g. `/inbox`. Module never hardcodes a path literal. */
  basePath: string;
  /** Authenticated user, or `null` when signed out. */
  currentUser: { userId: string; displayName?: string } | null;
  /** Fires when the satellite-api returns 401; consumer routes to its login page. */
  onUnauthorized: () => void;
  httpClient?: Client;
  /**
   * Resolves a conversation's listing into a brand-specific URL, or `null` when no link is
   * available. Module decides internal-vs-external rendering from the URL shape (relative
   * or same-origin → `<Link>`; absolute different-origin → `<a target="_blank">`). When
   * the resolver returns `null`, the module renders the listing as plain content.
   */
  getListingHref?: (listing: ListingLinkContext) => string | null;
  /**
   * Server-driven warning copy for the personal-contact (email/phone) `PersonalContactDetailsWarningModal`.
   * SSN warnings use the static `SSN_WARNING_DATA` from satellite-components and don't need this.
   * When `undefined`, the composer skips the personal-contact warning trigger entirely and sends
   * the message immediately. Consumers that have a `/getUserWarnings`-style endpoint should pass
   * the resolved data here; consumers without that endpoint leave this undefined and only the
   * SSN warning fires.
   */
  personalContactWarningData?: PersonalContactDetailsWarningData;
  externalLinkWarningData?: ExternalLinkWarningData;
  /**
   * Consumer-supplied analytics sink. The module fires each of the discriminants in
   * `InboxAnalyticsEvent` at its own trigger sites (page view, conversation click,
   * send, archive, report). The consumer translates each variant into its own
   * analytics envelope — see `apps/hotpads-web-next/src/analytics/inbox-events.ts`
   * for the HotPads adapter. When `undefined`, the module silently no-ops every fire
   * so consumers that don't care about analytics aren't forced to pass a stub.
   */
  onTrackEvent?: (event: InboxAnalyticsEvent) => void;
};

export type InboxProviderProps = InboxConfig & {
  children: ReactNode;
};

const InboxConfigContext = createContext<InboxConfig | null>(null);

export function InboxProvider({
  basePath,
  currentUser,
  onUnauthorized,
  httpClient,
  getListingHref,
  personalContactWarningData,
  externalLinkWarningData,
  onTrackEvent,
  children,
}: InboxProviderProps) {
  const value = useMemo<InboxConfig>(
    () => ({
      basePath,
      currentUser,
      onUnauthorized,
      httpClient,
      getListingHref,
      personalContactWarningData,
      externalLinkWarningData,
      onTrackEvent,
    }),
    [
      basePath,
      currentUser,
      onUnauthorized,
      httpClient,
      getListingHref,
      personalContactWarningData,
      externalLinkWarningData,
      onTrackEvent,
    ],
  );
  return <InboxConfigContext.Provider value={value}>{children}</InboxConfigContext.Provider>;
}

export function useInboxConfig(): InboxConfig {
  const value = useContext(InboxConfigContext);
  if (value === null) {
    throw new Error(
      'useInboxConfig() must be called inside an <InboxProvider>. Wrap the consuming subtree in <InboxProvider basePath="/inbox" currentUser={…} onUnauthorized={…} />.',
    );
  }
  return value;
}

export function useOptionalInboxConfig(): InboxConfig | null {
  return useContext(InboxConfigContext);
}
